import { Info, PictureAsPdf, TrendingUp } from '@mui/icons-material';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import Title from 'lib/designSystem/Title';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';

import { OperativeProfileData } from '../index';

const AccountRow = ({
  item,
}: {
  item: {
    name: string;
    total: number;
    arrearTrenches?: {
      name: string;
      amount: number;
      quantity: number;
    }[];
    quantity?: number;
  };
}) => {
  const renderQuantityTooltipContent = (quantity: number) => {
    if (!quantity) {
      return null;
    }
    return (
      <Box sx={{ p: 1, minWidth: '200px' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Cantidad de documentos
        </Typography>
        <Box>
          <Box
            key={quantity}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            sx={{
              p: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
              {quantity}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  const renderTooltipContent = () => {
    if (!item.arrearTrenches || item.arrearTrenches.length === 0) {
      return null;
    }

    return (
      <Box sx={{ p: 1, minWidth: '200px' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Detalle de {item.name}
        </Typography>
        <Box>
          {item.arrearTrenches.map((trench, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
              sx={{
                p: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontSize="0.875rem">
                {trench.name}
              </Typography>
              <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
                {trench.quantity}
              </Typography>
              <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
                {formatMoney(trench.amount)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={1}
    >
      <Typography variant="body2" color="text.secondary">
        {item.name}:
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight={600}>
          {formatMoney(item.total)}
        </Typography>
        {item.arrearTrenches && item.arrearTrenches.length > 0 && (
          <Tooltip
            title={renderTooltipContent()}
            placement="left"
            arrow
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  backgroundColor: 'white',
                  color: 'text.primary',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  fontSize: '0.875rem',
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                p: 0.5,

                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              <Info sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {item.quantity && (
          <Tooltip
            title={renderQuantityTooltipContent(item.quantity)}
            placement="left"
            arrow
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  backgroundColor: 'white',
                  color: 'text.primary',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  fontSize: '0.875rem',
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                p: 0.5,

                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              <Info sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const Summary = ({
  data,
  handleExportPDF,
}: {
  data: OperativeProfileData;
  handleExportPDF: () => void;
}) => {
  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Title
          title="Resumen de Cuenta Corriente"
          icon={<TrendingUp color="primary" sx={{ fontSize: 20 }} />}
          color="primary"
        />
        <Tooltip title="Exportar a PDF">
          <IconButton
            onClick={() => void handleExportPDF()}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            <PictureAsPdf />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        {/* Monto a Favor */}
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="success.main"
              gutterBottom
            >
              Monto a Favor
            </Typography>
            <Typography
              variant="h4"
              color="success.main"
              fontWeight={700}
              gutterBottom
            >
              {formatMoney(data.inFavorTotal ?? 0)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mt: 2 }}>
              {data?.inFavorAccounts?.map((item) => (
                <AccountRow key={item.name} item={item} />
              ))}
            </Box>
          </Box>
        </Grid>

        {/* Monto en Contra */}
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="error.main"
              gutterBottom
            >
              Monto en Contra
            </Typography>
            <Typography
              variant="h4"
              color="error.main"
              fontWeight={700}
              gutterBottom
            >
              {formatMoney(data?.againstTotal ?? 0)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mt: 2 }}>
              {data?.againstAccounts?.map((item) =>
                item.total > 0 ? (
                  <AccountRow key={item.name} item={item} />
                ) : null,
              )}
            </Box>
          </Box>
        </Grid>

        {/* Moras */}
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="warning.main"
              gutterBottom
            >
              Moras
            </Typography>
            <Typography
              variant="h4"
              color="warning.main"
              fontWeight={700}
              gutterBottom
            >
              {formatMoney(data?.arrearsTotal ?? 0)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mt: 2 }}>
              {data?.arrearsAccounts?.map((item) =>
                item.total > 0 ? (
                  <AccountRow key={item.name} item={item} />
                ) : null,
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Summary;
